// © 2026 AttainIP Engineering
/**
 * OAuth Callback Page — SPA + PKCE flow (same as SS UI)
 *
 * 1. Get code from URL params
 * 2. Exchange code directly with Azure AD using PKCE (no client secret)
 * 3. Send Azure AD tokens to Identity API to get internal tokens
 * 3b. …unless Identity answers with a Select-Organisation challenge instead of a
 *     token pair, which it does for an operator holding more than one active
 *     membership (RFC-0510 W05/W15). Then we render the picker and store NOTHING
 *     until the chosen organisation has been exchanged for a real token.
 * 4. Verify platform_admin or org_admin role (RFC-0212 A2)
 */
import { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  exchangeAzureTokens,
  getMe,
  requiresOrgSelection,
  type OrgSelectionRequiredResponse,
  type TokenResponse,
} from '@/api/identity';
import { SelectOrganisation } from '@/components/SelectOrganisation';
import { setTokens, clearTokens } from '@/lib/auth';

export function CallbackPage() {
  const [error, setError] = useState('');
  /** The Select-Organisation challenge, held in component state ONLY — the
   *  org-selection token it carries grants nothing and must never reach
   *  `setTokens` (RFC-0510 W28). */
  const [selection, setSelection] = useState<OrgSelectionRequiredResponse | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const processedRef = useRef(false);

  /**
   * Everything that turns a REAL token pair into an admin session. Shared by the
   * single-membership path (straight from `token-from-azure`) and the
   * multi-membership path (after the org-selection exchange), so a sign-in that
   * went through the picker continues exactly as an ordinary one does.
   */
  const completeLogin = useCallback(
    async (tokens: TokenResponse) => {
      setTokens(tokens.access_token, tokens.refresh_token);

      // Verify platform_admin or org_admin role (RFC-0211/0212 A2)
      const me = await getMe(tokens.access_token);
      const isPlatformAdmin = me.roles?.includes('platform_admin');
      const isOrgAdmin = me.roles?.includes('org_admin');
      if (!isPlatformAdmin && !isOrgAdmin) {
        clearTokens();
        setError(`Access denied — ${me.email} does not have the platform_admin or org_admin role`);
        return;
      }

      // Clear any stale queries from unauthenticated state
      queryClient.clear();
      // org_admin (without platform_admin) lands directly on their own firm
      // detail page — no Enterprises list (RFC-0212 A2).
      if (!isPlatformAdmin && isOrgAdmin && me.organization_id) {
        navigate(`/enterprises/${me.organization_id}`, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    },
    [navigate, queryClient],
  );

  /** Continue the sign-in the picker just unblocked. */
  const handleExchanged = useCallback(
    async (tokens: TokenResponse) => {
      try {
        await completeLogin(tokens);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      }
    },
    [completeLogin],
  );

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      return;
    }

    if (!code || !state) {
      setError('Missing authorization code or state parameter');
      return;
    }

    // Retrieve PKCE params from sessionStorage
    const storedState = sessionStorage.getItem('oauth_state');
    const codeVerifier = sessionStorage.getItem('oauth_code_verifier');
    const tokenEndpoint = sessionStorage.getItem('oauth_token_endpoint');
    const clientId = sessionStorage.getItem('oauth_client_id');
    const redirectUri = sessionStorage.getItem('oauth_redirect_uri');

    // Clear stored OAuth params
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_code_verifier');
    sessionStorage.removeItem('oauth_token_endpoint');
    sessionStorage.removeItem('oauth_client_id');
    sessionStorage.removeItem('oauth_redirect_uri');

    if (state !== storedState) {
      setError('Invalid state parameter — possible CSRF attack');
      return;
    }

    if (!codeVerifier || !tokenEndpoint || !clientId || !redirectUri) {
      setError('Missing PKCE parameters — please try logging in again');
      return;
    }

    (async () => {
      try {
        // Step 1: Exchange code with Azure AD directly (SPA + PKCE, no client secret)
        const azureRes = await fetch(tokenEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: clientId,
            grant_type: 'authorization_code',
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
          }),
        });

        if (!azureRes.ok) {
          const errData = await azureRes.json().catch(() => ({}));
          throw new Error(errData.error_description || errData.error || 'Azure AD token exchange failed');
        }

        const azureTokens = await azureRes.json();

        // Step 2: Exchange Azure AD tokens for internal Attain tokens
        const tokens = await exchangeAzureTokens(azureTokens.access_token, azureTokens.id_token);

        // Step 2b: Two disjoint outcomes (RFC-0510 W15). Narrow on the POSITIVE
        // discriminator, never on a missing field. A single-membership operator
        // never takes this branch and their sign-in is unchanged.
        if (requiresOrgSelection(tokens)) {
          // Deliberately no `setTokens` here: the org-selection token grants
          // nothing, and a session built from it 401s on every request.
          setSelection(tokens);
          return;
        }

        // Step 3-4: session, role check, redirect.
        await completeLogin(tokens);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
      }
    })();
  }, [searchParams, completeLogin]);

  if (selection && !error) {
    return <SelectOrganisation selection={selection} onExchanged={handleExchanged} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <a href={`${import.meta.env.BASE_URL}login`} className="text-sm text-blue-600 hover:underline">Back to login</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <p className="text-gray-500">Completing sign-in...</p>
    </div>
  );
}
